import React, { useState } from 'react';
import { Check } from 'lucide-react';
import ModalUnsuspendOption from './components/ModalUnsuspendOption';
import endpointConfig from '../../config/endpointConfig';
import { Step } from './EditSignature';
import { Certificate } from '../../entities/certificate';
import { rawHttp } from '../../http';
import styles from './my-signature-modal.module.scss';
import { Loader2Icon, OctagonPause, Trash, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AlertDialogTitle } from '@/components/ui/alert-dialog';
import notifyHB from '@/config/notifyHB';
import { toast } from '@/components/toast/toast';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import routeConfig from '@/config/routeConfig';

type UserInfo = {
	id_token: string;
	access_token: string;
	refresh_token: string;
	expires_in: number;
	token_type: string;
};

type SignatureModalProps = {
	certificate: Certificate;
	handleClose: () => void;
	setComponentState: (newStep: Step) => void;
	refreshCertificates: () => Promise<void>;
};

const SignatureModal: React.FC<SignatureModalProps> = ({
	certificate,
	handleClose,
	setComponentState,
	refreshCertificates,
}) => {
	const [customName, setCustomName] = useState(certificate.name);
	const [isUpdatingName, setIsUpdatingName] = useState(false);
	const [updateName, setUpdateName] = useState(false);

	const updatingName = (event: string) => {
		setUpdateName(false);
		setCustomName(event);
	};

	const updateCertificateName = async () => {
		setIsUpdatingName(true);
		try {
			const getToken: UserInfo = JSON.parse(
				localStorage.getItem('user-info') || '{}',
			);
			const authToken: string = getToken['access_token'];

			const updateResponse = await rawHttp.put(
				endpointConfig.updateCertificate(certificate.certificate_id),
				{
					custom_name: customName,
				},
				{
					headers: {
						Authorization: `Bearer ${authToken}`,
					},
				},
			);
			await refreshCertificates();

			if (updateResponse.status === 200) {
				setUpdateName(true);
			}
		} catch (error) {
			console.error(error);
			toast({ mode: 'destructive', description: 'Algo ha salido mal, intenta de nuevo más tarde' });
			notifyHB(
				'my-signatures',
				'SignatureModal',
				`Failed to update certificate name with ${error}`,
			);
		}
		setIsUpdatingName(false);
	};

	return (
		<div className={styles['my-signature-modal']}>
			<AlertDialogTitle className={styles['header']}>
				<span className={styles.title}>Editar Firma</span>
				<Button size="sm" variant="ghost" onClick={handleClose}>
					<X />
				</Button>
			</AlertDialogTitle>

			<div className={styles['content']}>
				<div className={styles['first-row']}>
					<div className={styles['certificate-field']}>
						<span className={styles['label']}>Tipo</span>
						<span className={styles['value']}>
							{certificate.type}
						</span>
					</div>
					<div className={styles['certificate-field']}>
						<span className={styles['label']}>No. de serie</span>
						<span className={styles['value']}>
							{certificate.certificate_id}
						</span>
					</div>
				</div>
				<div
					className={`${styles['certificate-field']} ${styles['expiry-date']}`}
				>
					<span className={styles['label']}>Fecha de expiración</span>
					<span className={styles['value']}>
						{DateTime.fromISO(
							certificate.expiry_date + 'Z',
						).toFormat('yyyy-MM-dd HH:mm')}
					</span>
				</div>
				<div className={styles['certificate-name']}>
					<Label>Nombre</Label>
					<div className={styles['input-and-button']}>
						<Input
							type="text"
							placeholder={certificate.name}
							disabled={certificate.status !== 'Active'}
							onChange={(event) =>
								updatingName(event.target.value)
							}
						/>
						<Button
							onClick={updateCertificateName}
							disabled={
								certificate.status !== 'Active' ||
								isUpdatingName
							}
						>
							{isUpdatingName && (
								<Loader2Icon className="animate-spin" />
							)}
							Actualizar
						</Button>
						{updateName ? (
							<Check
								style={{ position: 'relative', top: '5px' }}
							/>
						) : null}
					</div>
				</div>
				<div className={styles['certificate-name']}>
					<Label>Manuscrito</Label>
					<span>
						El manuscrito se gestiona en{' '}
						<Link to={routeConfig.customManuscript}>
							Mi Manuscrito
						</Link>
						.
					</span>
				</div>
				<div className={styles['actions-list']}>
					{(() => {
						if (certificate.status !== 'Active') {
							if (
								certificate.status_description === 'suspended'
							) {
								return (
									<ModalUnsuspendOption
										onReactivate={() =>
											setComponentState({
												step: 'confirmation',
												newStatus: 'reactivated',
											})
										}
										onRevoke={() => {
											setComponentState({
												step: 'confirmation',
												newStatus: 'revoked',
											});
										}}
									/>
								);
							} else if (
								certificate.status_description === 'revoked'
							) {
								return <Label>Firma Revocada</Label>;
							}
						} else {
							return (
								<>
									<Button
										variant="ghost"
										className={styles['danger-button']}
										onClick={() => {
											setComponentState({
												step: 'confirmation',
												newStatus: 'suspended',
											});
										}}
									>
										Suspender temporalmente
										<OctagonPause />
									</Button>
									<Button
										variant="ghost"
										className={styles['danger-button']}
										onClick={() => {
											setComponentState({
												step: 'confirmation',
												newStatus: 'revoked',
											});
										}}
									>
										Revocar
										<Trash />
									</Button>
								</>
							);
						}
					})()}
				</div>
			</div>
		</div>
	);
};

export default SignatureModal;
